import { FC, ReactNode } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { type Icon as PhosphorIcon } from '@phosphor-icons/react';

/**
 * A single document in a vertical list: tinted outlined card, leading icon,
 * name (+ optional monospace key), optional second line, and a trailing slot
 * for a chip or score.
 *
 * Extracted from the assessment summary's MissingStandardDocumentItem — the
 * remediation scope screen needs the same row twice over (documents to create,
 * documents to improve) and the shapes were identical apart from tone and what
 * sits on the trailing edge.
 */
export type DocumentListItemTone = 'warning' | 'ai' | 'info' | 'neutral';

interface ToneStyle {
  background: string;
  border: string;
  icon: string;
}

function toneStyles(theme: Theme): Record<DocumentListItemTone, ToneStyle> {
  const { palette } = theme;
  return {
    // The original MissingStandardDocumentItem asked for `warning.lighter`,
    // which this theme does not define — so it rendered with no background at
    // all. Uses the same alpha(warning) tint as CalloutBanner's warning tone,
    // which both exists and keeps the two consistent.
    warning: {
      background: alpha(palette.warning.main, 0.1),
      border: palette.warning.light,
      icon: palette.warning.light,
    },
    // Matches CalloutBanner's `ai` tone — the same "a machine produced this"
    // signal, so the two never disagree on what AI-generated looks like.
    ai: { background: palette.ocean[100], border: palette.ocean[300], icon: palette.ocean[700] },
    info: { background: palette.blue[100], border: palette.blue[300], icon: palette.blue[700] },
    neutral: {
      background: palette.background.paper,
      border: palette.grey[300],
      icon: palette.text.secondary,
    },
  };
}

export interface DocumentListItemProps {
  tone?: DocumentListItemTone;
  icon?: PhosphorIcon;
  filledIcon?: boolean;
  title: string;
  /** Rendered in monospace beside the title — a catalog key, a domain key, … */
  code?: string | null;
  /** Second line under the title. */
  subtitle?: ReactNode;
  /** Trailing edge — a Chip, a score, an action. */
  trailing?: ReactNode;
}

export const DocumentListItem: FC<DocumentListItemProps> = observer(
  ({ tone = 'neutral', icon: IconComponent, filledIcon = false, title, code, subtitle, trailing }) => {
    const theme = useTheme();
    const s = toneStyles(theme)[tone];

    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.25,
          px: 2,
          gap: 2,
          border: `1px solid ${s.border}`,
          borderRadius: 2,
          backgroundColor: s.background,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          {IconComponent && (
            <Box sx={{ p: 0.75, borderRadius: '100%', display: 'flex', flexShrink: 0 }}>
              <IconComponent
                color={s.icon}
                size={20}
                weight={filledIcon ? 'fill' : 'regular'}
              />
            </Box>
          )}

          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={{ minWidth: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="textSM_semiBold"
                sx={{
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={title}
              >
                {title}
              </Typography>
              {code && (
                <Typography
                  variant="textXS_regular"
                  sx={{ color: 'text.tertiary', fontFamily: 'monospace', flexShrink: 0 }}
                >
                  {code}
                </Typography>
              )}
            </Box>
            {subtitle != null && (
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {trailing != null && <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
      </Paper>
    );
  },
);
